import { useEffect, useState, useCallback } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase, SUPABASE_AUTH_KEY } from '@/integrations/supabase/client';
import { IS_NATIVE, platform } from '@/platform';
import { buildAuthRedirectUrl } from '@/lib/authRedirect';
import { signedOutPage } from '@/lib/safeRedirect';
import { DEMO_MODE, DEMO_USER } from '@/lib/demoMode';
import { logger } from '@/lib/logger';
import { clearCountryOverride } from '@/lib/marketChoice';
import { clearAnalyticsUser } from '@/lib/analytics/openpanel';
import { runBeforeSignOut, settleWithin } from '@/lib/signOutHooks';
import { localFirstWipe, signInKeysStored } from '@/lib/nativeSession';
import { authStorage, consumeBrowserAuthRemoval, webAuthStorageLockName } from '@/platform/authStorage';
import { wipeSessionData } from '@/lib/sessionWipe';
import { wipePerUserBrowserStorage } from '@/lib/perUserBrowserStorage';
import { purgeBookingDraft } from '@/lib/bookingPersistence';
import { withBrowserAuthMutation } from '@/lib/browserAuthMutationGate';
import { AuthWipeIncompleteError } from '@/lib/nativeAuthStorage';
import {
  WEB_SIGN_OUT_FENCE_KEY, isEndedWebSession, sameWebSession, sameWebSessionVersion,
  sessionFromStoredAuthArtifact, writeWebSignOutFence,
} from '@/lib/webSignOutFence';

// Global Auth Store (single supabase listener for the entire app)
interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  initialized: boolean;
  error: AuthError | null;
  /**
   * True when initialisation hit AUTH_INIT_TIMEOUT before Supabase answered.
   *
   * This is the difference between "we know there is no session" and "we do
   * not know yet, and we stopped blocking the app". Without it the two are
   * indistinguishable — `user: null, initialized: true, error: null` reads as
   * a definitive sign-out to every route guard, so a slow getSession() bounced
   * signed-in users to /auth.
   *
   * It is transient by construction: getSession() is still awaited past the
   * timeout, and the onAuthStateChange listener is already subscribed, so
   * whichever answers first clears this.
   */
  degraded: boolean;
}

interface AuthResult {
  success: boolean;
  error: AuthError | null;
}

/**
 * Which kind of account a signup is creating (#776).
 *
 * Routing only — it decides which onboarding a new account lands in, via
 * `profiles.is_helper`. Real privilege lives in `user_roles` and is not
 * settable from here.
 */
export type SignupRole = 'client' | 'helper';

// Constants
export const AUTH_REDIRECT_PATH = '/post-auth';
const RESET_PASSWORD_PATH = '/reset-password';
const AUTH_INIT_TIMEOUT = 5000;
/**
 * How long we keep saying "still resolving" after AUTH_INIT_TIMEOUT before
 * conceding that there is no session.
 *
 * Two stages, because the two failures are different. A getSession() that is
 * merely slow — waiting on the cross-tab Web Lock supabase-js v2 uses, or a
 * cold-start token refresh — resolves fine given a few more seconds, and
 * bouncing that user to /auth is wrong. A Supabase that is genuinely
 * unreachable never resolves, and pinning the app on a spinner forever is
 * also wrong. Stage one unblocks rendering without claiming anything about
 * the session; stage two concedes and restores the original behaviour.
 */
const AUTH_RESOLVE_GRACE = 10000;
const OAUTH_POPUP_TIMEOUT = 60000;
const POPUP_CHECK_INTERVAL = 2000;
const WEB_SIGN_OUT_STEP_TIMEOUT = 2500;

// Redirect URL helper
function buildRedirectUrl(path: string): string {
  // The origin comes from the platform — the page's own on the web, the App
  // Link origin natively (src/lib/authRedirect.ts) — so a tapped email link
  // opens the app instead of pointing at the WebView's https://localhost.
  return buildAuthRedirectUrl(path, window.location.search);
}

function newNonce(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID().replace(/-/g, '');
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
}

/**
 * Empties the rest of the secure store (§C auth row): after the auth keys
 * (removed and verified by the local-first wipe), anything else a signed-in
 * person left there. The local-first wipe already ran it (lib/sessionWipe.ts);
 * running it again covers a wipe that stopped short. Only while no session is
 * stored: a sign-in by link whose session lands once the first wipe lets go
 * of the store keeps its session and data, and one still under way keeps its
 * PKCE verifier (Codex on #1136 and #1137).
 */
async function wipeNativeSession(): Promise<void> {
  if (!IS_NATIVE) return;
  await wipeSessionData(platform.storage.secure, {
    stillEnded: async () => (await authStorage.getItem(SUPABASE_AUTH_KEY)) == null,
    keepKeys: () => signInKeysStored(async (key) => authStorage.getItem(key)),
  });
}

// Module-level store to avoid multiple onAuthStateChange subscriptions
const subscribers = new Set<(s: AuthState) => void>();
// In DEMO_MODE: initialize synchronously so every component gets the correct
// state on its very first render — eliminates the race condition where
// startAuthListenerOnce fires notify() before subscribers are added.
let started = DEMO_MODE;
let state: AuthState = DEMO_MODE
  ? {
      user: DEMO_USER as unknown as User,
      session: null,
      loading: false,
      initialized: true,
      error: null,
      degraded: false,
    }
  : {
      user: null,
      session: null,
      loading: true,
      initialized: false,
      error: null,
      degraded: false,
    };
let unsubscribeAuth: (() => void) | null = null;
/** Remove only storage proven to belong to the captured session. */
function clearBrowserAuthArtifactsFor(endedSession: Session, exactVersion = false): void {
  const sameSession = exactVersion ? sameWebSessionVersion : sameWebSession;
  try {
    const authKeys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i) || '';
      if (key.startsWith('sb-') || key.startsWith('supabase') || key.includes('auth-token')) {
        authKeys.push(key);
      }
    }
    for (const key of authKeys) {
      // Re-read immediately before removal. Building a list from an earlier
      // snapshot let another tab replace S1 with S2 while this tab was still
      // doing cleanup, after which the queued remove deleted S2 blindly.
      const stored = sessionFromStoredAuthArtifact(localStorage.getItem(key));
      if (stored && sameSession(stored, endedSession)) {
        localStorage.removeItem(key);
        localStorage.removeItem(`${key}-code-verifier`);
        localStorage.removeItem(`${key}-user`);
      }
    }
  } catch (storageError) {
    logger.warn('Failed to clear browser auth artifacts', storageError);
  }
}

function persistedWebSession(): Session | null {
  try {
    return sessionFromStoredAuthArtifact(localStorage.getItem(SUPABASE_AUTH_KEY));
  } catch (storageError) {
    logger.warn('Failed to read the persisted browser session', storageError);
    return null;
  }
}

function replacementFor(endedSession: Session, exactVersion = false): Session | null {
  const sameSession = exactVersion ? sameWebSessionVersion : sameWebSession;
  const persisted = persistedWebSession();
  if (persisted && !sameSession(persisted, endedSession)) return persisted;
  // A passive removal carries the exact stored version auth-js used. UI
  // state may still be older than that read and must not resurrect it after
  // the current invalid version has been removed.
  if (!exactVersion && state.session && !sameSession(state.session, endedSession)) return state.session;
  return null;
}

async function preserveReplacementWebSession(): Promise<AuthError | null> {
  const requirePersistedSession = () => {
    const persisted = persistedWebSession();
    // A replacement can itself sign out between cleanup and this lock. Never
    // recreate any captured token: storage is the authority, including null.
    if (!persisted || isEndedWebSession(persisted)) {
      throw new Error('The replacement browser session is no longer active');
    }
  };
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), WEB_SIGN_OUT_STEP_TIMEOUT);
  try {
    const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
    if (locks) {
      await locks.request(
        webAuthStorageLockName(SUPABASE_AUTH_KEY),
        { mode: 'exclusive', signal: controller.signal },
        () => withBrowserAuthMutation(requirePersistedSession, controller.signal),
      );
    } else {
      await withBrowserAuthMutation(requirePersistedSession, controller.signal);
    }
  } catch (storageError) {
    logger.warn('Failed to preserve the replacement browser session', storageError);
    return new AuthError('Replacement browser session could not be safely preserved');
  } finally {
    clearTimeout(timeout);
  }
  // Reload instead of calling auth.setSession: the latter completes
  // asynchronously and can overwrite an S3 login that lands after this check.
  // Reload rehydrates whichever session is newest when navigation occurs.
  try { window.location.reload(); } catch { /* navigation may be unavailable in tests */ }
  return null;
}

async function reportReplacementPreservationFailure(error: AuthError): Promise<AuthResult> {
  setState((prev) => ({ ...prev, loading: false, error }));
  await showToast(
    'Session update did not finish safely',
    'Your current sign-in could not be synchronized across browser tabs. Close other tabs, then reload this page.',
    'destructive',
  );
  return { success: false, error };
}

interface WebCleanupResult {
  replacement: Session | null;
  accountDataCleared: boolean;
}

/**
 * Run the destructive, account-wide browser cleanup under the same Web Lock
 * that our auth storage adapter uses for every session write. This closes the last gap
 * between "there is no replacement" and wiping the old account's data: a
 * sign-in in another tab must now land either before the check (and is
 * preserved) or after the wipe (and cannot be erased by it).
 *
 * Every writer also takes the shared IndexedDB transaction gate. If a Web
 * Lock is absent or times out, cleanup can acquire that same inner gate
 * directly and still exclude replacement writes. Lock ordering is always
 * Web Lock -> transaction; a transaction never waits for a Web Lock.
 */
async function clearEndedWebSession(endedSession: Session | null, exactVersion = false): Promise<WebCleanupResult> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  const cleanup = (): WebCleanupResult => {
    const replacementBeforeClear = endedSession
      ? replacementFor(endedSession, exactVersion)
      : (persistedWebSession() ?? state.session);
    if (replacementBeforeClear) return { replacement: replacementBeforeClear, accountDataCleared: false };

    if (endedSession) clearBrowserAuthArtifactsFor(endedSession, exactVersion);
    const replacementAfterClear = endedSession
      ? replacementFor(endedSession, exactVersion)
      : (persistedWebSession() ?? state.session);
    if (replacementAfterClear) return { replacement: replacementAfterClear, accountDataCleared: false };

    wipePerUserBrowserStorage();
    purgeBookingDraft();
    clearCountryOverride();
    return { replacement: null, accountDataCleared: true };
  };
  if (locks) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), WEB_SIGN_OUT_STEP_TIMEOUT);
    try {
      return await locks.request(
        webAuthStorageLockName(SUPABASE_AUTH_KEY),
        { mode: 'exclusive', signal: controller.signal },
        () => withBrowserAuthMutation(cleanup, controller.signal),
      );
    } catch (lockError) {
      logger.warn('Could not acquire the browser auth lock; trying the shared transaction gate', lockError);
    } finally {
      clearTimeout(timeout);
    }
  }
  try {
    return await withBrowserAuthMutation(cleanup);
  } catch (gateError) {
    logger.warn('Could not acquire the browser transaction gate for account-data cleanup', gateError);
    return {
      replacement: endedSession ? replacementFor(endedSession, exactVersion) : (persistedWebSession() ?? state.session),
      accountDataCleared: false,
    };
  }
}

const notify = () => {
  subscribers.forEach((cb) => cb(state));
};

const setState = (updater: (prev: AuthState) => AuthState) => {
  state = updater(state);
  notify();
};

async function endBrowserSessionLocally(endedSession: Session | null, exactVersion = false): Promise<void> {
  const cleanup = await clearEndedWebSession(endedSession, exactVersion);
  // A delayed event for S1 must not tear down S2. This can happen when a
  // refresh resolves after sign-out and another tab has already signed in.
  if (cleanup.replacement && endedSession) {
    const error = await preserveReplacementWebSession();
    if (error) await reportReplacementPreservationFailure(error);
    return;
  }
  if (cleanup.replacement) return;
  if (!cleanup.accountDataCleared) {
    logger.warn('Could not safely finish passive browser session cleanup');
    const replacement = endedSession ? replacementFor(endedSession, exactVersion) : (persistedWebSession() ?? state.session);
    if (replacement && endedSession) {
      const error = await preserveReplacementWebSession();
      if (error) await reportReplacementPreservationFailure(error);
      return;
    }
    if (replacement) return;
    const error = new AuthError('Browser account data cleanup could not finish safely');
    setState((prev) => ({ ...prev, loading: false, error }));
    await showToast('Session cleanup did not finish', 'Reload the page to retry clearing this browser session.', 'destructive');
    return;
  }
  clearAnalyticsUser();
  setState((prev) => ({
    ...prev,
    session: null,
    user: null,
    loading: false,
    initialized: true,
    error: null,
    degraded: false,
  }));
}

async function startAuthListenerOnce() {
  if (started) return;
  started = true;

  // Short-circuit for demo mode — inject a fake user immediately
  if (DEMO_MODE) {
    setState(() => ({
      session: null,
      user: DEMO_USER as unknown as User,
      loading: false,
      initialized: true,
      error: null,
      degraded: false,
    }));
    return;
  }

  // mounted flag: set to false once any resolution path completes so the
  // safety timeout cannot overwrite a valid session with user:null.
  let mounted = true;

  // Safety timeout: if Supabase never responds, unblock the app.
  //
  // It must unblock WITHOUT asserting the user is signed out. getSession() is
  // still awaited below and the onAuthStateChange listener is already
  // subscribed, so this is genuinely "not known yet", not "not signed in".
  // Previously this wrote session/user null with error null, which every route
  // guard reads as a definitive sign-out — that is what bounced signed-in
  // admins to /auth, and why the state looked identical to a real logout.
  //
  // session/user are deliberately NOT overwritten here: if the listener has
  // already delivered a session in the meantime, clobbering it would throw
  // away the answer we were waiting for.
  let concedeTimeout: ReturnType<typeof setTimeout> | null = null;

  const initTimeout = setTimeout(() => {
    if (!mounted) return;
    logger.warn('Auth initialization timeout - still resolving, not treating as signed out');
    setState((prev) => ({
      ...prev,
      loading: false,
      initialized: true,
      degraded: true,
    }));

    // Stage two. If nothing has answered by now, Supabase is not merely slow,
    // and holding the app on a spinner indefinitely is its own bug. Concede to
    // the original behaviour — no session — so the app stays usable.
    concedeTimeout = setTimeout(() => {
      if (!mounted) return;
      logger.warn('Auth did not resolve within the grace window - proceeding without session');
      setState((prev) => ({
        ...prev,
        session: null,
        user: null,
        loading: false,
        initialized: true,
        degraded: false,
      }));
    }, AUTH_RESOLVE_GRACE);
  }, AUTH_INIT_TIMEOUT);

  // Both timers must die on any resolution, or the concede path can still fire
  // after a session has landed and log the user straight back out.
  const clearInitTimers = () => {
    clearTimeout(initTimeout);
    if (concedeTimeout) clearTimeout(concedeTimeout);
  };

  try {
    // Set up a single listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      clearInitTimers();
      mounted = false;
      if (session && isEndedWebSession(session)) {
        logger.warn('Ignored a stale auth session that resolved after local sign-out');
        void endBrowserSessionLocally(session);
        return;
      }
      if (!IS_NATIVE && !session && _event === 'SIGNED_OUT') {
        const removal = consumeBrowserAuthRemoval(SUPABASE_AUTH_KEY);
        const removedSession = removal ? sessionFromStoredAuthArtifact(removal.expected) : null;
        const persisted = persistedWebSession();
        if (persisted && !isEndedWebSession(persisted) && (!removal || (removal.refused && !removedSession))) {
          // A remote SIGNED_OUT carries no source-session identity. A real
          // removal happened before the broadcast; an unfenced session still
          // stored now is a replacement (or a refused stale remote cleanup).
          // A refused cleanup of malformed storage likewise cannot attribute
          // this replacement to the invalid value it originally read.
          void preserveReplacementWebSession()
            .then((error) => { if (error) void reportReplacementPreservationFailure(error); });
          return;
        }
        const endedSession = removedSession ?? state.session;
        // Explicit logout fences the whole logical session. An unfenced
        // failed refresh only owns the exact token version it read, even if
        // S2's broadcast has already changed state.session in this tab.
        const exactVersion = !!removal && (!endedSession || !isEndedWebSession(endedSession));
        void endBrowserSessionLocally(endedSession, exactVersion);
        return;
      }
      // Unified handler: update once per event
      // Whoever answers first clears `degraded` — this listener or the
      // getSession() below. That is what makes the degraded window bounded
      // rather than sticky.
      setState((prev) => ({
        ...prev,
        session,
        user: session?.user ?? null,
        loading: false,
        initialized: true,
        error: null,
        degraded: false,
      }));

      // Native replacement detection and secure-store cleanup have their own
      // local-first protocol. Keep passive native state changes synchronous
      // here; the browser Web Lock path above must never inspect native state.
      if (IS_NATIVE && !session && _event === 'SIGNED_OUT') {
        clearAnalyticsUser();
        wipePerUserBrowserStorage();
        purgeBookingDraft();
      }

            // TH-053 Pillar 1: keep profiles.last_login_at fresh for re-engagement cohorts.
            // Deferred + fire-and-forget so it never blocks the auth state update above.
            if (session?.user) {
                      setTimeout(() => {
                                Promise.resolve(supabase.rpc('update_last_login')).catch((e) => logger.warn('Failed to update last_login_at', e));
                      }, 0);
            }
    });

    const onWebSignOutFenceChange = (event: StorageEvent) => {
      if (event.key !== WEB_SIGN_OUT_FENCE_KEY || !state.session || !isEndedWebSession(state.session)) return;
      logger.info('Ending this tab session after sign-out in another tab');
      void endBrowserSessionLocally(state.session);
    };
    if (!IS_NATIVE) window.addEventListener('storage', onWebSignOutFenceChange);
    unsubscribeAuth = () => {
      subscription.unsubscribe();
      if (!IS_NATIVE) window.removeEventListener('storage', onWebSignOutFenceChange);
    };

    // Get initial session
    const { data, error } = await supabase.auth.getSession();
    clearInitTimers();
    mounted = false;

    const initialSession = data.session && isEndedWebSession(data.session) ? null : data.session ?? null;
    if (data.session && !initialSession) {
      void endBrowserSessionLocally(data.session);
      return;
    }
    setState((prev) => ({
      ...prev,
      session: initialSession,
      user: initialSession?.user ?? null,
      loading: false,
      initialized: true,
      error: (error as AuthError) ?? null,
      degraded: false,
    }));
  } catch (err) {
    clearInitTimers();
    mounted = false;
    logger.error('Auth initialization error', err);
    // A thrown error IS a definitive answer — it is recorded in `error` and a
    // caller can act on it. That is the opposite of the timeout case, so this
    // clears `degraded` rather than setting it.
    setState((prev) => ({
      ...prev,
      loading: false,
      initialized: true,
      error: err as AuthError,
      degraded: false,
    }));
  }
}

// Toast helper
async function showToast(title: string, description: string, variant: 'default' | 'destructive' = 'default') {
  try {
    const { toast } = await import('@/hooks/use-toast');
    toast({ title, description, variant });
  } catch (_) {
    // no-op
  }
}

export function useAuth() {
  const [local, setLocal] = useState<AuthState>(state);

  useEffect(() => {
    // Start global listener (runs once; no-op in DEMO_MODE since started=true)
    startAuthListenerOnce();

    // Subscribe to global store
    const cb = (s: AuthState) => setLocal(s);
    subscribers.add(cb);
    // Sync current module state immediately — covers the race condition where
    // notify() fired before this subscriber was added (e.g. on first mount).
    setLocal({ ...state });
    return () => {
      subscribers.delete(cb);
      // Do NOT unsubscribe supabase here; it's global for the app lifetime
    };
  }, []);

  // Auth methods operate on global state for consistency
  const signUp = useCallback(async (
    email: string,
    password: string,
    firstName?: string,
    lastName?: string,
    phone?: string,
    /**
     * Which kind of account this is, ONLY when the caller genuinely already
     * knows — e.g. a deep link back into an in-progress helper application.
     * `signup_role` is read by handle_new_user (20261225000000) to set
     * profiles.is_helper: 'helper' -> true, 'client' -> false, and —
     * deliberately — omitted/undefined -> NULL (undeclared), which is what
     * sends a fresh, ordinary signup to the /profile-creation chooser instead
     * of guessing. This used to default to 'client' when omitted, which
     * silently skipped that chooser for every plain signup; do not
     * reintroduce a fallback here.
     */
    signupRole?: SignupRole,
    acceptedTermsVersion?: string,
    /** The sign-up form's choices (src/lib/consents.ts), recorded with the account (20270580000000). */
    consents?: Record<string, unknown>,
  ): Promise<AuthResult> => {
    try {
      setState((p) => ({ ...p, loading: true, error: null }));

      const redirectUrl = buildRedirectUrl(AUTH_REDIRECT_PATH);
      const fullName = `${firstName || ''} ${lastName || ''}`.trim();

      const { data, error } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            first_name: firstName || '',
            last_name: lastName || '',
            full_name: fullName,
            phone: phone || '',
            // Present only when actually known — see the param comment above.
            ...(signupRole ? { signup_role: signupRole } : {}),
            ...(acceptedTermsVersion ? { accepted_terms_version: acceptedTermsVersion } : {}),
            ...(consents ? { consents } : {}),
          },
        },
      });

      if (error) {
        setState((p) => ({ ...p, loading: false, error }));
        await showToast('Sign up failed', error.message || 'Unable to create account', 'destructive');
        return { success: false, error };
      }

      // Log successful signup
      const { logAuditEvent } = await import('@/lib/auditLogger');
      if (data.user) {
        await logAuditEvent({
          userId: data.user.id,
          eventType: 'auth_signup',
          eventData: { 
            email: email.trim().toLowerCase(),
            firstName: firstName || '',
            lastName: lastName || '',
          },
          status: 'success',
        });
      }

      /*
       * Deliberately conditional wording, and NOT a claim that an email was
       * sent.
       *
       * Supabase has email-enumeration protection on: signing up with an
       * address that already has a confirmed account returns a FAKE SUCCESS
       * — no error, a user object whose `identities` array is empty — so an
       * attacker cannot use this form to discover which emails are
       * registered. Nothing is created and nothing is emailed in that case.
       *
       * This used to assert, unconditionally, that a confirmation link had
       * been sent — simply untrue for that path: reported live by a client who
       * re-registered an existing address, was told to check their inbox, and
       * waited for an email that was never sent (auth.users showed
       * confirmation_sent_at unchanged from their original 2025 signup, while
       * our own audit log recorded a fresh auth_signup success).
       *
       * The honest phrasing covers both outcomes without distinguishing them
       * — which is the whole point of the protection, so we do not undo it
       * here. Detecting the duplicate via `identities.length === 0` and
       * saying so plainly would re-open exactly the enumeration oracle
       * Supabase is closing, and would also mis-handle the
       * exists-but-never-confirmed case, so it is a separate product
       * decision rather than part of this copy fix.
       */
      await showToast(
        'Almost there',
        "If that email isn't already registered, a confirmation link is on its way — click it to verify your account. Already have an account? Sign in instead.",
      );
      setState((p) => ({ ...p, loading: false }));
      return { success: true, error: null };
    } catch (err) {
      const error = err as AuthError;
      setState((p) => ({ ...p, loading: false, error }));
      await showToast('Sign up failed', 'An unexpected error occurred', 'destructive');
      return { success: false, error };
    }
  }, []);

  const signIn = useCallback(async (email: string, password: string): Promise<AuthResult> => {
    try {
      setState((p) => ({ ...p, loading: true, error: null }));
      
      const { logAuditEvent } = await import('@/lib/auditLogger');

      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });
      
      if (error) {
        setState((p) => ({ ...p, loading: false, error }));
        
        // Log failed auth attempt
        await logAuditEvent({
          eventType: 'auth_signin',
          eventData: { email: email.trim().toLowerCase() },
          status: 'failure',
          errorMessage: error.message,
        });
        
        let msg = 'Unable to sign in';
        if (error.message.includes('Invalid login credentials')) msg = 'Invalid email or password';
        else if (error.message.includes('Email not confirmed')) msg = 'Please check your email and confirm your account';
        else if (error.message.includes('Too many requests')) msg = 'Too many login attempts. Please try again later';
        await showToast('Sign in failed', msg, 'destructive');
        return { success: false, error };
      }
      
      if (data.session) {
        // Log successful auth
        await logAuditEvent({
          userId: data.session.user.id,
          eventType: 'auth_signin',
          eventData: { email: email.trim().toLowerCase() },
          status: 'success',
        });
        
        await showToast('Welcome back!', 'You have been signed in successfully.');
        setState((p) => ({ ...p, loading: false, session: data.session, user: data.session.user, error: null }));
        return { success: true, error: null };
      }
      
      setState((p) => ({ ...p, loading: false }));
      return { success: false, error: new Error('No session returned') as AuthError };
    } catch (err) {
      const error = err as AuthError;
      setState((p) => ({ ...p, loading: false, error }));
      await showToast('Sign in failed', 'An unexpected error occurred', 'destructive');
      return { success: false, error };
    }
  }, []);

  const signInWithGoogle = useCallback(async (): Promise<AuthResult> => {
    try {
      setState((p) => ({ ...p, loading: true, error: null }));

      if (IS_NATIVE) {
        // Native Google Sign-In (mobile assessment §C Google row, §R.25): the
        // platform's plugin returns an id token and Supabase verifies it
        // against the web client id listed under its Google provider (D7).
        // No browser round trip, so nothing here derives from window.location.
        const nonce = newNonce();
        let token: string | null;
        try {
          token = await platform.auth.googleIdToken({ nonce });
        } catch (err) {
          const error = err as AuthError;
          logger.error('Native Google sign-in unavailable', err);
          setState((p) => ({ ...p, loading: false, error }));
          await showToast('Google sign in unavailable', error.message || 'Google sign-in is not set up on this build yet.', 'destructive');
          return { success: false, error };
        }
        if (!token) {
          const error = new Error('Google sign-in was cancelled.') as AuthError;
          setState((p) => ({ ...p, loading: false, error }));
          return { success: false, error };
        }
        const { error } = await supabase.auth.signInWithIdToken({ provider: 'google', token, nonce });
        if (error) {
          logger.error('Google id-token sign-in error', error);
          setState((p) => ({ ...p, loading: false, error }));
          await showToast('Google sign in failed', error.message, 'destructive');
          return { success: false, error };
        }
        setState((p) => ({ ...p, loading: false }));
        return { success: true, error: null };
      }

      const redirectTo = buildRedirectUrl(AUTH_REDIRECT_PATH);
      const inIframe = window.self !== window.top;

      logger.debug('Google OAuth: starting sign in', { inIframe });

      if (inIframe) {
        // In preview/embed environments, Google blocks being loaded in an iframe.
        // Open in new window and set up listener for completion
        const { data, error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo,
            skipBrowserRedirect: true,
            queryParams: { access_type: 'offline', prompt: 'consent' },
          },
        });

        if (error) {
          logger.error('Google OAuth error (iframe flow)', error);
          setState((p) => ({ ...p, loading: false, error }));
          await showToast('Google sign in failed', error.message, 'destructive');
          return { success: false, error };
        }

        if (data?.url) {
          logger.debug('Google OAuth: opening in new window');
          
          // Shared cleanup so every exit path tears down the poll interval, the
          // safety timeout, and the message listener — otherwise the 2s poll
          // leaks and keeps calling refreshSession() forever.
          const oauthTimers: {
            checkInterval?: ReturnType<typeof setInterval>;
            safetyTimeout?: ReturnType<typeof setTimeout>;
          } = {};
          const cleanupOAuth = () => {
            if (oauthTimers.checkInterval) clearInterval(oauthTimers.checkInterval);
            if (oauthTimers.safetyTimeout) clearTimeout(oauthTimers.safetyTimeout);
            window.removeEventListener('message', authCompleteHandler);
          };

          // Listen for auth completion message from popup
          const authCompleteHandler = async (event: MessageEvent) => {
            // Security: Validate origin
            if (event.origin !== window.location.origin) {
              logger.warn('Ignoring message from untrusted origin');
              return;
            }

            if (event.data?.type !== 'GOOGLE_AUTH_COMPLETE') {
              return;
            }

            logger.debug('Google OAuth: auth completion message received');
            cleanupOAuth();

            try {
              if (!event.data.success) {
                logger.error('Google OAuth: popup reported auth failure', event.data.error);
                setState((p) => ({ ...p, loading: false }));
                await showToast(
                  'Google sign in failed', 
                  event.data.error || 'Authentication failed', 
                  'destructive'
                );
                return;
              }

              const at = event.data.tokens?.access_token;
              const rt = event.data.tokens?.refresh_token;

              if (at && rt) {
                logger.debug('Google OAuth: setting session from popup tokens');
                const { data: setData, error: setError } = await supabase.auth.setSession({
                  access_token: at,
                  refresh_token: rt,
                });

                if (setError) {
                  logger.error('Google OAuth: failed to set session', setError);
                  setState((p) => ({ ...p, loading: false }));
                  await showToast('Google sign in failed', 'Failed to establish session', 'destructive');
                  return;
                }

                if (setData?.session) {
                  logger.debug('Google OAuth: session established in opener');
                  const establishedSession = setData.session;
                  setState((p) => ({
                    ...p,
                    session: establishedSession,
                    user: establishedSession.user,
                    loading: false,
                    error: null,
                  }));
                  await showToast('Welcome!', 'You have been signed in successfully.');
                  window.location.assign(buildRedirectUrl(AUTH_REDIRECT_PATH));
                  return;
                }
              }

              logger.debug('Google OAuth: attempting session refresh as fallback');
              const { data: sessionData, error: sessionError } = await supabase.auth.refreshSession();
              
              if (sessionData?.session) {
                logger.debug('Google OAuth: session refreshed in opener');
                const refreshedSession = sessionData.session;
                setState((p) => ({
                  ...p,
                  session: refreshedSession,
                  user: refreshedSession.user,
                  loading: false,
                  error: null,
                }));
                await showToast('Welcome!', 'You have been signed in successfully.');
                window.location.assign(buildRedirectUrl(AUTH_REDIRECT_PATH));
              } else {
                logger.error('Google OAuth: failed to establish session', sessionError);
                setState((p) => ({ ...p, loading: false }));
                await showToast('Google sign in failed', 'Could not verify authentication', 'destructive');
              }
            } catch (e) {
              logger.error('Google OAuth: error handling popup auth completion', e);
              setState((p) => ({ ...p, loading: false }));
              await showToast('Google sign in failed', 'An unexpected error occurred', 'destructive');
            }
          };
          
          window.addEventListener('message', authCompleteHandler);
          
          // Open OAuth flow in popup
          const popup = window.open(data.url, 'google-auth', 'width=600,height=700');
          
          // Safety net: stop polling after the popup timeout so a popup that is
          // never closed (or a failed handler path) can't leak the interval.
          oauthTimers.safetyTimeout = setTimeout(() => {
            cleanupOAuth();
            setState((p) => (p.loading ? { ...p, loading: false } : p));
          }, OAUTH_POPUP_TIMEOUT);

          // Fallback: check session every 2 seconds if popup is closed
          oauthTimers.checkInterval = setInterval(async () => {
            if (popup?.closed) {
              cleanupOAuth();

                logger.debug('Google OAuth: popup closed, refreshing session');
                const { data: sessionData } = await supabase.auth.refreshSession();
                if (sessionData?.session) {
                  logger.debug('Google OAuth: session found after popup closed');
                  const foundSession = sessionData.session;
                  setState((p) => ({
                    ...p,
                    session: foundSession,
                    user: foundSession.user,
                    loading: false,
                    error: null,
                  }));
                  await showToast('Welcome!', 'You have been signed in successfully.');
                  // Navigate to /post-auth with preserved redirect
                  window.location.assign(buildRedirectUrl(AUTH_REDIRECT_PATH));
                } else {
                  setState((p) => ({ ...p, loading: false }));
                }
            }
          }, POPUP_CHECK_INTERVAL);
          
          return { success: true, error: null };
        }

        const fallbackError = new Error('No OAuth URL returned') as AuthError;
        setState((p) => ({ ...p, loading: false, error: fallbackError }));
        await showToast('Google sign in failed', 'Could not start Google login', 'destructive');
        return { success: false, error: fallbackError };
      }

      // Normal flow (not embedded)
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo,
          queryParams: { access_type: 'offline', prompt: 'consent' },
        },
      });

      if (error) {
        logger.error('Google OAuth error', error);
        setState((p) => ({ ...p, loading: false, error }));
        let errorMessage = error.message;
        if (error.message.includes('redirect_uri_mismatch')) {
          errorMessage = 'Redirect URI configuration error. Please check Google OAuth settings.';
        } else if (error.message.includes('access_denied')) {
          errorMessage = 'Google sign-in was cancelled or access was denied.';
        } else if (error.message.includes('invalid_client')) {
          errorMessage = 'Google OAuth client configuration error. Please contact support.';
        }
        await showToast('Google sign in failed', errorMessage, 'destructive');
        return { success: false, error };
      }

      logger.debug('Google OAuth initiated successfully');
      return { success: true, error: null };
    } catch (err) {
      const error = err as AuthError;
      logger.error('Google OAuth exception', err);
      setState((p) => ({ ...p, loading: false, error }));
      await showToast('Google sign in failed', 'An unexpected error occurred', 'destructive');
      return { success: false, error };
    }
  }, []);

  const signInWithMagicLink = useCallback(async (email: string): Promise<AuthResult> => {
    try {
      setState((p) => ({ ...p, loading: true, error: null }));
      const { error } = await supabase.auth.signInWithOtp({ 
        email: email.trim().toLowerCase(), 
        options: { emailRedirectTo: buildRedirectUrl(AUTH_REDIRECT_PATH) } 
      });
      if (error) {
        setState((p) => ({ ...p, loading: false, error }));
        await showToast('Magic link failed', error.message, 'destructive');
        return { success: false, error };
      }
      await showToast('Check your email', "We've sent you a sign-in link and a 6-digit code.");
      setState((p) => ({ ...p, loading: false }));
      return { success: true, error: null };
    } catch (err) {
      const error = err as AuthError;
      setState((p) => ({ ...p, loading: false, error }));
      await showToast('Magic link failed', 'An unexpected error occurred', 'destructive');
      return { success: false, error };
    }
  }, []);

  /**
   * Signs in with the 6-digit code from the sign-in email. The link in that
   * email only finishes in the browser that asked for it (PKCE), and on
   * Android it opens in the phone's browser, never the app (owner, 31 Oct: a
   * new client in the app saw "Authentication Error"). The code works
   * wherever it is typed. Type 'email' accepts the code from either email:
   * Magic link, or Confirm signup when signInWithOtp created the account.
   */
  const verifyEmailCode = useCallback(async (email: string, code: string): Promise<AuthResult> => {
    try {
      setState((p) => ({ ...p, loading: true, error: null }));
      const { data, error } = await supabase.auth.verifyOtp({
        email: email.trim().toLowerCase(),
        token: code.replace(/\s+/g, ''),
        type: 'email',
      });
      if (error) {
        setState((p) => ({ ...p, loading: false, error }));
        return { success: false, error };
      }
      const session = data.session;
      if (session) {
        setState((p) => ({ ...p, loading: false, session, user: session.user, error: null }));
        return { success: true, error: null };
      }
      setState((p) => ({ ...p, loading: false }));
      return { success: false, error: new Error('No session returned') as AuthError };
    } catch (err) {
      const error = err as AuthError;
      setState((p) => ({ ...p, loading: false, error }));
      return { success: false, error };
    }
  }, []);

  const resetPassword = useCallback(async (email: string): Promise<AuthResult> => {
    try {
      setState((p) => ({ ...p, loading: true, error: null }));
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), { redirectTo: buildAuthRedirectUrl(RESET_PASSWORD_PATH, '') });
      if (error) {
        setState((p) => ({ ...p, loading: false, error }));
        await showToast('Reset failed', error.message, 'destructive');
        return { success: false, error };
      }
      await showToast('Check your email', "We've sent you a password reset link.");
      setState((p) => ({ ...p, loading: false }));
      return { success: true, error: null };
    } catch (err) {
      const error = err as AuthError;
      setState((p) => ({ ...p, loading: false, error }));
      await showToast('Reset failed', 'An unexpected error occurred', 'destructive');
      return { success: false, error };
    }
  }, []);

  // Native (M2 brief §2.5): the session lives in the Keystore, and auth-js
  // removes it only once the server sign-out succeeds, so an offline
  // sign-out would leave it on the phone (Codex on #1093). The wipe runs
  // FIRST, before anything that waits on the network; the global revocation
  // inside it and the audit record after it are best effort.
  const nativeSignOut = useCallback(async (returnTo?: string): Promise<AuthResult> => {
    let uid: string | undefined;
    let accessToken: string | undefined;
    try {
      // Read from the client's memory: no network.
      const { data: { session: liveSession } } = await supabase.auth.getSession();
      uid = liveSession?.user?.id;
      accessToken = liveSession?.access_token;
    } catch {
      // Nothing to attribute the audit row to; the wipe still runs.
    }
    try {
      // Runs the before-sign-out hooks first (push token release), then wipes.
      await localFirstWipe();
    } catch (wipeError) {
      if (!(wipeError instanceof AuthWipeIncompleteError)) logger.error('Native sign-out failed', wipeError);
      setState((p) => ({ ...p, loading: false }));
      await showToast(
        'Sign-out did not finish on this phone',
        'Your sign-in could not be cleared from this phone. Restart Kanfy, then sign out again.',
        'destructive',
      );
      return { success: false, error: new AuthError('Sign-out did not finish on this phone') };
    }
    if (uid && accessToken) {
      const token = accessToken;
      const userId = uid;
      void import('@/lib/auditLogger')
        .then(({ logAuditEventAs }) =>
          logAuditEventAs(token, { userId, eventType: 'auth_signout', eventData: { scope: 'global' }, status: 'success' }),
        )
        .catch((auditError) => logger.warn('Failed to record sign-out in the audit log', auditError));
    }
    await wipeNativeSession();
    clearCountryOverride();
    setState((p) => ({ ...p, user: null, session: null, loading: false, error: null }));
    await showToast('Signed out', 'You have been logged out successfully.');
    const authPage = signedOutPage(returnTo);
    try { window.location.replace(authPage); } catch { window.location.href = authPage; }
    return { success: true, error: null };
  }, []);

  // returnTo: a page to come back to after signing in again (the WhatsApp
  // link asks for a fresh sign-in); sign-out is a full-page load, so it rides
  // on the /auth URL rather than on the router.
  const signOut = useCallback(async (options?: { returnTo?: string }): Promise<AuthResult> => {
    const authPage = signedOutPage(options?.returnTo);
    let signingOutSession: Session | null = null;
    try {
      setState((p) => ({ ...p, loading: true, error: null }));
      // Native: the local-first sign-out (M2, brief §2.5), which works offline.
      if (IS_NATIVE) return await nativeSignOut(options?.returnTo);

      // Capture the session before fencing it. Once the fence is persisted, a
      // late refresh in this tab (or another one) is allowed to clear shared
      // auth storage without taking away the token needed for revocation.
      let liveSession = state.session;
      if (!liveSession) {
        try {
          liveSession = persistedWebSession();
        } catch {
          // A blocked storage read still falls through to auth-js below.
        }
      }
      if (!liveSession) {
        const lookup = await settleWithin(supabase.auth.getSession(), WEB_SIGN_OUT_STEP_TIMEOUT);
        if (!lookup.timedOut) liveSession = lookup.value.data.session;
      }
      signingOutSession = liveSession;
      try {
        if (liveSession) await writeWebSignOutFence(liveSession);
      } catch (fenceError) {
        // No audit, hook, revocation, or cleanup is safe until every tab can
        // reject a queued refresh. Return here; the outer fallback is destructive.
        logger.warn('Could not persist browser sign-out protection', fenceError);
        const error = new AuthError('Browser sign-out protection could not be saved');
        setState((p) => ({ ...p, loading: false, error }));
        await showToast(
          'Sign-out did not start',
          'This browser could not save sign-out protection. Reload the page, then try again.',
          'destructive',
        );
        return { success: false, error };
      }

      // Web. Audit and device-release hooks need the current token, but every
      // wait is bounded and the persisted fence stops a late refresh restoring it.

      // Record the sign-out BEFORE the session is destroyed. log_audit_event
      // binds audit_logs.user_id to auth.uid() server-side (20260801103000),
      // so the same call made after supabase.auth.signOut() lands with
      // user_id = NULL — a row that says somebody signed out without saying
      // who, which is worse than no row because it reads like a record.
      //
      // Capture the token once, then log through an isolated non-persisting
      // client. If this task resumes after its deadline it still names the
      // account that signed out, never a replacement session.
      const audit = await settleWithin((async () => {
        try {
          if (liveSession?.user?.id && liveSession.access_token) {
            const { logAuditEventAs } = await import('@/lib/auditLogger');
            await logAuditEventAs(liveSession.access_token, {
              userId: liveSession.user.id,
              eventType: 'auth_signout',
              eventData: { scope: 'local' },
              status: 'success',
            });
          }
        } catch (auditError) {
          logger.warn('Failed to record sign-out in the audit log', auditError);
        }
      })(), WEB_SIGN_OUT_STEP_TIMEOUT);
      if (audit.timedOut) logger.warn('Timed out recording sign-out audit; continuing local sign-out');

      // Last authenticated calls (e.g. releasing this phone's push token) —
      // once the token is revoked below they would be refused. Bounded, and
      // never throws.
      await runBeforeSignOut();

      // Revoke exactly the captured session. A global request that outlives its
      // timeout can also revoke a later login for the same account; local scope
      // remains safe even when the network call completes after this function.
      const revocation = liveSession?.access_token
        ? await settleWithin(
            supabase.auth.admin.signOut(liveSession.access_token, 'local'),
            WEB_SIGN_OUT_STEP_TIMEOUT,
          )
        : { timedOut: false as const, value: { error: null } };
      if (revocation.timedOut) {
        logger.warn('Server sign-out timed out; local session cleanup will continue');
      }
      const error = revocation.timedOut ? null : revocation.value.error;

      // Clear only artifacts owned by the captured session. The account-data
      // wipe is fenced with the storage adapter's cross-tab auth lock, so a new
      // login cannot land between the final replacement check and the wipe.
      try {
        const cleanup = await clearEndedWebSession(liveSession);
        if (liveSession && cleanup.replacement) {
          const preservationError = await preserveReplacementWebSession();
          if (preservationError) return await reportReplacementPreservationFailure(preservationError);
          logger.info('Preserved a replacement session after the previous session signed out');
          await showToast('Previous session signed out', 'Your current session remains signed in.');
          return { success: true, error: null };
        }
        if (!cleanup.accountDataCleared) {
          const cleanupError = new AuthError('Local account data cleanup could not acquire the auth storage lock');
          setState((p) => ({ ...p, loading: false, error: cleanupError }));
          await showToast(
            'Sign-out did not finish safely',
            'Your account data could not be cleared from this browser. Reload the page, then sign out again.',
            'destructive',
          );
          return { success: false, error: cleanupError };
        }
        await wipeNativeSession();
      } catch (storageError) {
        logger.warn('Failed to clear local auth artifacts during sign out', storageError);
      }

      // Consider session-not-found a successful signout
      const isSessionError = error && (
        error.message?.includes('session_not_found') ||
        error.message?.includes('Session not found') ||
        error.message?.includes('Auth session missing') ||
        error.message?.includes('No session')
      );

      const remoteRevokeUnconfirmed = revocation.timedOut || (error && !isSessionError);
      if (remoteRevokeUnconfirmed) {
        logger.warn('Signed out locally, but server session revocation was not confirmed', error);
      }
      // The local session and account data are already gone. A server error
      // must never put this browser back into the account it just cleared.
      setState((p) => ({ ...p, user: null, session: null, loading: false, error: null }));
      await showToast(
        remoteRevokeUnconfirmed ? 'Signed out on this device' : 'Signed out',
        remoteRevokeUnconfirmed
          ? 'We could not confirm server sign-out. This device was cleared; try again when you are online.'
          : 'You have been logged out successfully.',
        remoteRevokeUnconfirmed ? 'destructive' : 'default',
      );
      try { window.location.replace(authPage); } catch { window.location.href = authPage; }
      return { success: true, error: null };
    } catch (err) {
      // Fallback: clear state and force reload even if something unexpected happens
      const replacementSession = signingOutSession ? replacementFor(signingOutSession) : null;
      if (signingOutSession && replacementSession) {
        const preservationError = await preserveReplacementWebSession();
        if (preservationError) return await reportReplacementPreservationFailure(preservationError);
        logger.warn('Sign-out failed after a replacement session became active; preserving it', err);
        await showToast('Previous session sign-out was not confirmed', 'Your current session remains signed in.', 'destructive');
        return { success: false, error: err as AuthError };
      }
      try {
        const cleanup = await clearEndedWebSession(signingOutSession);
        if (signingOutSession && cleanup.replacement) {
          const preservationError = await preserveReplacementWebSession();
          if (preservationError) return await reportReplacementPreservationFailure(preservationError);
          logger.warn('Sign-out failed after a replacement session became active; preserving it', err);
          await showToast('Previous session sign-out was not confirmed', 'Your current session remains signed in.', 'destructive');
          return { success: false, error: err as AuthError };
        }
        if (!cleanup.accountDataCleared) {
          const cleanupError = new AuthError('Local account data cleanup could not acquire the auth storage lock');
          setState((p) => ({ ...p, loading: false, error: cleanupError }));
          await showToast(
            'Sign-out did not finish safely',
            'Your account data could not be cleared from this browser. Reload the page, then sign out again.',
            'destructive',
          );
          return { success: false, error: cleanupError };
        }
        await wipeNativeSession();
      } catch (storageError) {
        logger.warn('Failed to clear local auth artifacts after sign out error', storageError);
      }
      setState((p) => ({ ...p, user: null, session: null, loading: false, error: null }));
      await showToast('Signed out', 'You have been logged out successfully.');
      try { window.location.replace(authPage); } catch { window.location.href = authPage; }
      return { success: true, error: err as AuthError };
    }
  }, [nativeSignOut]);

  const refreshSession = useCallback(async (): Promise<AuthResult> => {
    try {
      setState((p) => ({ ...p, loading: true }));
      const { data, error } = await supabase.auth.refreshSession();
      if (error) {
        setState((p) => ({ ...p, loading: false, error }));
        return { success: false, error };
      }
      if (data.session) {
        const refreshedSession = data.session;
        setState((p) => ({ ...p, session: refreshedSession, user: refreshedSession.user, loading: false, error: null }));
      } else {
        setState((p) => ({ ...p, loading: false }));
      }
      return { success: true, error: null };
    } catch (err) {
      const error = err as AuthError;
      setState((p) => ({ ...p, loading: false, error }));
      return { success: false, error };
    }
  }, []);

  return {
    // State
    user: local.user,
    session: local.session,
    loading: local.loading,
    initialized: local.initialized,
    error: local.error,
    degraded: local.degraded,

    // Auth methods
    signUp,
    signIn,
    signInWithGoogle,
    signInWithMagicLink,
    verifyEmailCode,
    resetPassword,
    signOut,
    refreshSession,

    // Computed state — in demo mode session is always null; treat user presence as authenticated
    isAuthenticated: DEMO_MODE ? !!local.user : (!!local.user && !!local.session),
    // `degraded` counts as loading on purpose. Every route guard in the app
    // already branches on isLoading, so this is what turns "we do not know
    // yet" into a spinner rather than a redirect to /auth — without touching
    // each guard individually. It is bounded by AUTH_RESOLVE_GRACE, after
    // which the concede path clears it and the original behaviour returns.
    isLoading: local.loading || !local.initialized || local.degraded,
  };
}
